import type { ReactNode } from "react";
import { Sparkline } from "./Sparkline";
import { useBleed } from "./Page";

/**
 * The one surface every screen is built on. Radius, edge width and shadow are
 * all tenant tokens (`radiusLg`, `borderStyle`, `shadowLevel`), so an org that
 * chose square corners or no borders sees that here too.
 */
export function Card({
  children,
  className = "",
  fill = false,
}: {
  children: ReactNode;
  className?: string;
  /**
   * D-15 — as tall as its rows, but never taller than the page: a short list
   * ends where its rows end instead of leaving an empty card below them, and a
   * long one stops at the bottom edge and scrolls ONLY the table inside.
   * No `flex-1` on purpose; `min-h-0` is what lets it shrink to the space left
   * (without it the card grows and the page scrolls instead).
   */
  fill?: boolean;
}) {
  const bleed = useBleed();
  // On a list page (`<Page bleed>`) the table is the page: no card edge, and
  // it takes all the height left, so there is no box to sit empty.
  if (fill && bleed) {
    return (
      <section
        className={`flex min-h-0 flex-1 flex-col overflow-hidden border-t border-rule bg-raised first:border-t-0 ${className}`}
      >
        {children}
      </section>
    );
  }
  return (
    <section
      className={[
        "rounded-lg border-token border-rule bg-raised shadow-card",
        fill ? "flex min-h-0 flex-col overflow-hidden" : "",
        className,
      ].join(" ")}
    >
      {children}
    </section>
  );
}

export function CardHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-center justify-between gap-3 border-b border-rule/70 px-5 py-4">
      <div className="min-w-0">
        <h2 className="font-title text-[15px] leading-snug font-semibold tracking-[-0.01em] text-ink">
          {title}
        </h2>
        {description ? <p className="mt-0.5 text-sm text-muted">{description}</p> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </header>
  );
}

export function CardBody({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`px-5 py-4 ${className}`}>{children}</div>;
}

/**
 * A metric tile.
 *
 * Deliberately has NO coloured icon chip. The old dashboard gave each tile a
 * differently-coloured icon — blue, green, orange, grey — which carried no
 * meaning and made four unrelated tiles compete for attention. Colour here is
 * reserved for `delta`, where up/down genuinely means good/bad, and for the
 * `attention` tone, which marks the one tile that needs acting on.
 *
 * The number is the point of the tile, so it gets the size, the weight and
 * the tabular figures; everything else recedes.
 */
export function StatCard({
  label,
  value,
  hint,
  delta,
  trend,
  tone = "neutral",
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  /** Recent history for a sparkline beside the value. */
  trend?: readonly number[];
  /** e.g. `{ value: "+12%", direction: "up" }`. `flat` renders neutral. */
  delta?: { value: string; direction: "up" | "down" | "flat" };
  /** `attention` draws a brand rail and tint when a tile needs acting on. */
  tone?: "neutral" | "attention";
}) {
  const attention = tone === "attention";
  return (
    <div
      className={[
        "relative overflow-hidden rounded-lg border-token px-4 pt-4 pb-4 shadow-card sm:px-5",
        attention ? "border-brand-border bg-brand-light" : "border-rule bg-raised",
      ].join(" ")}
    >
      {attention ? (
        <span aria-hidden="true" className="absolute inset-x-0 top-0 h-[3px] bg-brand" />
      ) : null}

      <div className="flex items-center justify-between gap-3">
        <p
          className={`text-[11px] font-semibold tracking-[0.08em] uppercase ${
            attention ? "text-brand-ink" : "text-muted"
          }`}
        >
          {label}
        </p>
        <ModuleMark attention={attention} />
      </div>

      <div className="mt-2.5 flex items-end justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
          {/* Proportional figures: tabular digits look loose at display size. */}
          <span className="font-title text-[2rem] leading-none font-semibold tracking-[-0.02em] text-ink">
            {value}
          </span>
          {delta ? (
            <span
              className={`text-[13px] font-medium whitespace-nowrap ${
                delta.direction === "up"
                  ? "text-success"
                  : delta.direction === "down"
                    ? "text-danger"
                    : "text-muted"
              }`}
            >
              {delta.direction === "up" ? "▲ " : delta.direction === "down" ? "▼ " : ""}
              {delta.value}
            </span>
          ) : null}
        </div>
        {trend ? <Sparkline values={trend} /> : null}
      </div>

      {hint ? <p className="mt-2 text-sm text-muted">{hint}</p> : null}
    </div>
  );
}

/**
 * Three QR modules in an L — the product's mark, drawn at the corner of a
 * metric tile. Decoration only; it carries no meaning, so it stays quiet.
 */
function ModuleMark({ attention }: { attention: boolean }) {
  const tone = attention ? "bg-brand" : "bg-rule";
  return (
    <span aria-hidden="true" className="grid grid-cols-2 gap-[3px]">
      <span className={`size-[5px] rounded-[1px] ${tone}`} />
      <span className={`size-[5px] rounded-[1px] ${tone}`} />
      <span className={`size-[5px] rounded-[1px] ${tone}`} />
      <span className="size-[5px]" />
    </span>
  );
}

/** Row of metric tiles. Keeps every dashboard's top strip identical. */
export function StatGrid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">{children}</div>;
}
